import { Add } from '@mui/icons-material';
import { Box, Button } from '@mui/material';
import { GridRowEditStopParams, GridRowEditStopReasons, MuiEvent, useGridApiRef } from '@mui/x-data-grid-pro';
import { useCustomSnackbar } from '@procureai/ui-mui';
import { formatDate } from '@procureai/util';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useUserProfileAbsences } from '../../hooks/useUserProfileAbsences/useUserProfileAbsences';
import { AstroAbsenceListItem } from '@/generated/gql/graphql';
import { I18nNamespace } from '~/common/types';
import { DataGridCustom } from '~/components/DataGridCustom';
import { SimpleTextOverlay } from '~/components/DataGridCustom/overlays/SimpleTextOverlay/SimpleTextOverlay.tsx';
import { useUserDetail } from '~/modules/Module.hooks';

import { useUserProfileAbsenceGridActions } from './UserProfileAbsenceGridForm.actions';
import { useUserProfleAbsenceDataGridFormConfig } from './UserProfileAbsenceGridForm.config';
import { AbsenceGridRow } from './UserProfileAbsenceGridForm.types';

const cancelFocusOut = (params: GridRowEditStopParams, event: MuiEvent) => {
  if (params.reason === GridRowEditStopReasons.rowFocusOut) {
    event.defaultMuiPrevented = true;
  }
};

export const AbsenceGridForm = () => {
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'UserProfileAbsenceWidget' });
  const { enqueueErrorSnackbar } = useCustomSnackbar();
  const { userId } = useUserDetail();

  const apiRef = useGridApiRef();

  const { userProfileAbsences, deleteAbsenceEntry, upsertAbsenceEntry, editable } = useUserProfileAbsences({ userId });
  const { config } = useUserProfleAbsenceDataGridFormConfig({ apiRef, editable });

  const { actionColumn, rowModesModel, onRowModesModelChange, addRow } = useUserProfileAbsenceGridActions({
    apiRef,
    editable,
    onDelete: async (id) => {
      await deleteAbsenceEntry(`${id}`);
    },
  });

  const columns = useMemo(
    () => (actionColumn ? config.columns.concat(actionColumn) : config.columns),
    [config, actionColumn],
  );

  const rows = useMemo(() => {
    if (userProfileAbsences) {
      return userProfileAbsences.map((absence: AstroAbsenceListItem) => ({
        id: absence.id,
        absentTime: {
          from: new Date(absence.start),
          to: new Date(absence.end),
        },
      }));
    }
    return [];
  }, [userProfileAbsences]);

  const handleRowUpdate = async ({ ...row }: AbsenceGridRow) => {
    if (!row.absentTime) {
      throw new Error('error.absenceTimeNotSet');
    }
    const { from, to } = row.absentTime || {};
    if (!from || !to) {
      throw new Error('error.invaldRangeSelected');
    }

    await upsertAbsenceEntry({
      id: row.id,
      start: formatDate(from),
      end: formatDate(to),
    });

    return {
      ...row,
    };
  };

  const handleError = (error: Error) => {
    enqueueErrorSnackbar(t(error.message));
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '400px',
        }}
      >
        <DataGridCustom
          apiRef={apiRef}
          editMode="row"
          density="compact"
          getRowId={(row) => row.id}
          rows={rows}
          columns={columns}
          rowModesModel={rowModesModel}
          onRowModesModelChange={onRowModesModelChange}
          processRowUpdate={handleRowUpdate}
          onProcessRowUpdateError={handleError}
          onRowEditStop={cancelFocusOut}
          disableColumnReorder
          disableColumnMenu
          disableColumnFilter
          disableMultipleColumnsSorting
          slots={{
            noRowsOverlay: () => <SimpleTextOverlay label={t('datagrid.noRows')} />,
          }}
        />
      </Box>
      {editable ? (
        <Box
          sx={{
            mt: 3,
            marginTop: 1,
            display: 'flex',
          }}
        >
          <Button variant="outlined" onClick={addRow} startIcon={<Add />}>
            {t('actions.addRow')}
          </Button>
        </Box>
      ) : null}
    </>
  );
};
